/**
 * /l/:country[/:state[/:city]] — encyclopedia-style location article.
 *
 * Left rail: jump-to-section anchors (bookmarking / tabs).
 * Right rail: curated outbound links (Wikipedia, gov, tourism, maps…).
 * Body: 48 possible sections, only the populated ones render.
 *
 * Markdown-lite renderer: preserves paragraph breaks, bolds `**...**`,
 * and auto-linkifies URLs via the existing <Linkify /> component.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, ArrowLeft, Globe, ExternalLink, MapPin, BookOpenText, Sparkles,
} from "lucide-react";
import Linkify from "@/components/Linkify";
import AutoLinkText from "@/components/AutoLinkText";
import CommunityHub from "@/components/CommunityHub";
import LocalPostFab from "@/components/LocalPostFab";
import StatePlaces from "@/components/StatePlaces";
import DiasporaJourney from "@/components/DiasporaJourney";
import LocalNewsFeed from "@/components/LocalNewsFeed";
import LocationWeatherPanel from "@/components/LocationWeatherPanel";
import CityPulse from "@/components/CityPulse";
import VillageWeatherWidget from "@/components/VillageWeatherWidget";
import PortalView from "@/components/PortalView";
import PublisherWidget from "@/components/publisher/PublisherWidget";
import ChatPulseThread from "@/components/chatpulse/ChatPulseThread";
import { slugifyLinkCaption } from "@/pages/LinkReferencePage";
import LocationJsonLd from "@/components/LocationJsonLd";
import { paletteFor } from "@/lib/palette";

const SECTION_LABELS = {
  basic:           "Basic information",
  overview:        "Overview",
  history:         "History",
  geography:       "Geography",
  topography:      "Topography",
  weather:         "Weather & climate",
  population:      "Population",
  government:      "Government",
  economy:         "Economy",
  industries:      "Industries",
  agriculture:     "Agriculture",
  housing:         "Housing",
  neighborhoods:   "Neighborhoods",
  transportation:  "Transportation",
  infrastructure:  "Infrastructure",
  education:       "Education",
  healthcare:      "Healthcare",
  emergency:       "Emergency services",
  businesses:      "Businesses",
  restaurants:     "Restaurants & food",
  tourism:         "Tourism",
  recreation:      "Recreation",
  parks_nature:    "Parks & nature",
  waterways:       "Rivers, lakes & waterways",
  arts_culture:    "Arts & culture",
  religion:        "Religion",
  sports:          "Sports",
  events:          "Events",
  media:           "Media",
  personalities:   "Prominent personalities",
  companies:       "Notable companies",
  sister_cities:   "Sister cities",
  local_orgs:      "Local organisations",
  education_stats: "Education statistics",
  utilities:       "Public utilities",
  safety:          "Safety",
  environment:     "Environment",
  future:          "Future development",
  demographics:    "Demographics",
  facts:           "Interesting facts",
  faq:             "FAQ",
  images:          "Images",
  videos:          "Videos",
  maps:            "Maps",
  community:       "Social Pulse community",
  seo:             "Metadata & search",
  references:      "References",
};

const LINK_KIND_COLOR = {
  wikipedia: "#3366cc",
  gov:       "#166534",
  tourism:   "#DB2777",
  wikivoyage:"#7C3AED",
  map:       "#0891B2",
  business:  "#B25D38",
};

// Detectors for media URLs so we can render richer blocks than plain text.
const IMG_RX = /\.(jpg|jpeg|png|webp|gif|svg)(\?.*)?$/i;
const WIKIMEDIA_RX = /commons\.wikimedia\.org\/wiki\/Special:FilePath\//i;
const YT_ID_RX = /(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{6,})/i;

function isImageUrl(url) {
  return IMG_RX.test(url) || WIKIMEDIA_RX.test(url);
}
function ytIdOf(url) {
  const m = String(url || "").match(YT_ID_RX);
  return m ? m[1] : null;
}

/**
 * Map a location doc into a SocialPulse Publisher location_id.
 * Returns null when the slug isn't recognised — the widget then renders nothing.
 *
 * Example: {country:"india", state:"kerala", city:"pala"} → "IN-KL-pala"
 *          {country:"india", state:"kerala"}              → "IN-KL"
 */
function publisherLocationIdFor(doc) {
  if (!doc) return null;
  const country = (doc.country || "").toLowerCase();
  const state = (doc.state || "").toLowerCase();
  const city = (doc.city || "").toLowerCase();
  const COUNTRY_MAP = { india: "IN", "united states": "US", usa: "US", uk: "GB" };
  const KERALA_ALIASES = { kerala: "KL" };
  const STATE_MAP = {
    IN: { ...KERALA_ALIASES,
      "tamil nadu": "TN", karnataka: "KA", maharashtra: "MH", "andhra pradesh": "AP",
      telangana: "TG", "west bengal": "WB", gujarat: "GJ", punjab: "PB",
      rajasthan: "RJ", "uttar pradesh": "UP", bihar: "BR", odisha: "OR",
      goa: "GA", haryana: "HR", "madhya pradesh": "MP", jharkhand: "JH",
      chhattisgarh: "CG", assam: "AS", meghalaya: "ML", "arunachal pradesh": "AR",
      manipur: "MN", mizoram: "MZ", nagaland: "NL", tripura: "TR",
      sikkim: "SK", uttarakhand: "UT", "himachal pradesh": "HP",
      "jammu and kashmir": "JK", ladakh: "LA", puducherry: "PY",
      delhi: "DL", chandigarh: "CH",
    },
  };
  const cc = COUNTRY_MAP[country];
  if (!cc) return null;
  const sc = STATE_MAP[cc]?.[state];
  if (!sc) return cc;
  if (city) {
    // Slug the city name (lowercase, spaces→dashes, strip diacritics)
    const citySlug = city.trim().toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
    return citySlug ? `${cc}-${sc}-${citySlug}` : `${cc}-${sc}`;
  }
  return `${cc}-${sc}`;
}

// Turn markdown-lite (**bold**, blank-line paragraphs, headings, lists, media
// URLs) into React nodes. Kept minimal so existing prose remains stable.
function renderBody(text) {
  if (!text) return null;
  const paragraphs = text.split(/\n{2,}/);
  return paragraphs.map((p, pi) => {
    const trimmed = p.trim();
    if (!trimmed) return null;

    // Standalone image URL → render inline image
    const urlOnly = trimmed.match(/^(https?:\/\/\S+)$/);
    if (urlOnly && isImageUrl(urlOnly[1])) {
      return (
        <figure key={pi} className="my-4">
          <img
            src={urlOnly[1]}
            alt=""
            loading="lazy"
            crossOrigin="anonymous"
            referrerPolicy="no-referrer"
            className="rounded-xl w-full max-h-[420px] object-cover border"
            style={{ borderColor: "rgba(0,0,0,0.08)" }}
          />
        </figure>
      );
    }

    // Standalone YouTube URL → clickable thumbnail card
    if (urlOnly) {
      const vid = ytIdOf(urlOnly[1]);
      if (vid) {
        return (
          <a
            key={pi}
            href={urlOnly[1]}
            target="_blank"
            rel="noopener noreferrer"
            className="my-4 block relative rounded-xl overflow-hidden group border"
            style={{ borderColor: "rgba(0,0,0,0.08)" }}
          >
            <img
              src={`https://img.youtube.com/vi/${vid}/hqdefault.jpg`}
              alt=""
              loading="lazy"
              className="w-full h-64 object-cover"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/25 group-hover:bg-black/45 transition">
              <div className="w-16 h-16 rounded-full flex items-center justify-center shadow-lg" style={{ background: "#ef4444" }}>
                <svg className="w-8 h-8 text-white ml-1" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
            </div>
          </a>
        );
      }
    }

    // Headings
    if (trimmed.startsWith("### ")) {
      return (
        <h4 key={pi} className="font-display text-lg mt-5 mb-2">
          {renderInline(trimmed.slice(4))}
        </h4>
      );
    }
    if (trimmed.startsWith("## ")) {
      return (
        <h3 key={pi} className="font-display text-xl mt-6 mb-2">
          {renderInline(trimmed.slice(3))}
        </h3>
      );
    }

    // Bulleted list — every non-blank line begins with "- "
    const lines = p.split("\n").filter((l) => l.trim().length > 0);
    if (lines.length > 0 && lines.every((l) => l.trim().startsWith("- "))) {
      return (
        <ul key={pi} className="list-disc pl-6 mb-3 space-y-1">
          {lines.map((l, i) => (
            <li key={i} className="text-base leading-relaxed">
              {renderInline(l.trim().slice(2))}
            </li>
          ))}
        </ul>
      );
    }
    // Numbered list — every non-blank line begins with "N. "
    if (lines.length > 0 && lines.every((l) => /^\d+\.\s/.test(l.trim()))) {
      return (
        <ol key={pi} className="list-decimal pl-6 mb-3 space-y-1">
          {lines.map((l, i) => (
            <li key={i} className="text-base leading-relaxed">
              {renderInline(l.trim().replace(/^\d+\.\s+/, ""))}
            </li>
          ))}
        </ol>
      );
    }

    return (
      <p key={pi} className="text-base leading-relaxed mb-3 whitespace-pre-wrap">
        {renderInline(p)}
      </p>
    );
  });
}

function renderInline(text) {
  // Split on **bold** markers, keeping the delimiters.
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    // Two-stage: KG entity auto-linking first (people, places, orgs, events,
    // diseases), then Linkify for bare URLs inside the plain-text chunks
    // that survive KG matching. AutoLinkText passes each remaining string
    // through the `renderPlain` hook, which wraps it in <Linkify>.
    return (
      <AutoLinkText
        key={i}
        text={part}
        perEntityMax={1}
        renderPlain={(s) => <Linkify text={s} />}
      />
    );
  });
}

export default function LocationArticle() {
  const params = useParams();
  const loc = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const portalMode = searchParams.get("view") === "portal";
  const exitPortal = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("view"); next.delete("s"); next.delete("sub");
    setSearchParams(next, { replace: true });
  };
  const enterPortal = () => {
    const next = new URLSearchParams(searchParams);
    next.set("view", "portal");
    setSearchParams(next, { replace: false });
  };
  // Build the slug from the URL path after `/l/` — supports 1, 2 or 3 levels.
  const slug = useMemo(() => {
    const raw = loc.pathname.replace(/^\/l\/?/, "").replace(/\/+$/, "");
    return raw.toLowerCase();
  }, [loc.pathname]);

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [publishName, setPublishName] = useState("");

  // Fetch (or refetch on interval while publishing).
  const fetchDoc = async () => {
    try {
      const r = await http.get(`/locations/${slug}`);
      setDoc(r.data);
      setErr(null);
      // If we were publishing and the article now has real content, stop polling.
      const secCount = Object.values(r.data?.sections || {}).filter((v) => String(v || "").trim()).length;
      if (publishing && secCount >= 5) setPublishing(false);
      return r.data;
    } catch (e) {
      setErr(e?.response?.data?.detail || "Not found");
      return null;
    }
  };

  useEffect(() => {
    if (!slug) { setLoading(false); return; }
    setLoading(true);
    setErr(null);
    setPublishing(false);
    fetchDoc().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // If the article is missing OR sparse, kick off publish + poll every 8s
  // until sections fill in.
  useEffect(() => {
    if (loading) return;
    const secCount = Object.values(doc?.sections || {}).filter((v) => String(v || "").trim()).length;
    const missing = !!err && !doc;
    const sparse = doc && secCount < 5;
    if (!missing && !sparse) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await http.post("/locations/kick", { slug });
        if (cancelled) return;
        setPublishName(r.data?.name || slug);
        setPublishing(true);
      } catch {
        /* ignore — user can still hit Home */
      }
    })();
    const iv = setInterval(fetchDoc, 3000);
    return () => { cancelled = true; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, err, doc, slug]);

  if (loading) {
    return <div className="max-w-5xl mx-auto py-14 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  }
  const sectionsFilled = Object.values(doc?.sections || {}).filter((v) => String(v || "").trim()).length;
  const showPublishingCard = (!doc && err) || (doc && sectionsFilled < 5);
  if (showPublishingCard) {
    // Prettify the slug for the friendly message.
    const prettyLocation = publishName || (() => {
      const parts = slug.split("/").filter(Boolean);
      const last = parts[parts.length - 1] || slug;
      return last.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
    })();
    return (
      <div className="max-w-xl mx-auto py-24 px-6 text-center" data-testid="location-publishing">
        <div className="flex justify-center mb-6">
          <div className="relative w-16 h-16 rounded-full flex items-center justify-center" style={{ background: "linear-gradient(135deg, #EEF2FF 0%, #E9D5FF 100%)" }}>
            <Sparkles className="w-8 h-8" style={{ color: "#7C3AED" }} />
            <Loader2 className="w-16 h-16 absolute -inset-0 animate-spin opacity-30" style={{ color: "#7C3AED" }} />
          </div>
        </div>
        <h1 className="font-display text-3xl leading-tight" style={{ color: "#2563EB" }} data-testid="location-publishing-title">
          The article about {prettyLocation} will be published shortly
        </h1>
        <p className="text-sm mt-4" style={{ color: "var(--wofy-text-soft)" }}>
          Our editorial AI is researching and writing a comprehensive profile — usually ready in about 15–20 seconds.
          This page will refresh automatically as soon as it's live.
        </p>
        <div className="mt-8 flex items-center justify-center gap-3">
          <Link to="/" className="wofy-btn-primary inline-flex" data-testid="location-publishing-home">
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>
          <button
            onClick={fetchDoc}
            className="text-sm underline"
            style={{ color: "#2563EB" }}
            data-testid="location-publishing-refresh"
          >
            Refresh now
          </button>
        </div>
      </div>
    );
  }

  const palette = paletteFor(doc.slug);
  const sections = doc.sections || {};
  const populated = Object.keys(SECTION_LABELS).filter((k) => sections[k] && String(sections[k]).trim());
  const crumb = [doc.country, doc.state, doc.city].filter(Boolean).join(" · ");

  return (
    <article className="max-w-6xl mx-auto px-4 lg:px-6 py-8" data-testid={`location-article-${doc.slug}`}>
      {/* Portal view — full NIC-style tree. When the URL carries
          `?view=portal`, we swap the article for the portal shell. */}
      {portalMode && (
        <PortalView locationSlug={doc.slug} name={doc.name} onExit={exitPortal} />
      )}
      {portalMode ? null : (<>
      {/* JSON-LD structured data — indexed by search engines, invisible
          to users. Emits schema.org/Place + BreadcrumbList. */}
      <LocationJsonLd doc={doc} />

      {/* Article ↔ Portal toggle — the "portal view" is a NIC-district
          style tree of ~180 subsections. Available on every location
          but only surfaced as a CTA for country/state/county pages
          where the tree is dense with real data. */}
      <div className="mb-4 flex justify-end">
        <button
          onClick={enterPortal}
          className="text-xs font-mono uppercase tracking-widest rounded-full px-3 py-1.5 border-2 hover:brightness-95 transition inline-flex items-center gap-1.5"
          style={{ borderColor: "#DBEAFE", background: "#EFF6FF", color: "#1D4ED8" }}
          title="Open the full district portal tree for this location"
          data-testid="location-portal-toggle"
        >
          Portal view →
        </button>
      </div>

      {/* City Pulse — real-time hero widget (time, weather, top news,
          busiest POI category). Only surfaces for city/town/county
          articles where all four tiles are meaningful. */}
      {(doc.kind === "city" || doc.kind === "town" || doc.kind === "county") && (
        <CityPulse
          slug={doc.slug}
          name={doc.name}
          kind={doc.kind}
          lat={doc.lat}
          lng={doc.lng}
          state={doc.state}
          country={doc.country}
          timezone={doc.timezone}
        />
      )}

      {/* Village / heritage-site "Weather on the founding date" — auto
          renders when the doc supplies a founding date, otherwise ships
          a date-picker so visitors can time-travel to any historical
          date at this place. */}
      {(doc.kind === "village" || doc.kind === "heritage_site" || doc.kind === "town") && (
        <div className="mb-4">
          <VillageWeatherWidget
            name={doc.name}
            foundingDate={doc.founded_date || doc.founding_date}
            lat={doc.lat ?? doc.coord_lat}
            lon={doc.lng ?? doc.coord_lng}
            location={[doc.name, doc.state, doc.country].filter(Boolean).join(", ")}
          />
        </div>
      )}

      {/* Community Hub — surfaced at the top so mobile visitors see local
          activity (events, marketplace, family trees…) without scrolling. */}
      <div className="mb-3" data-testid="community-hub-top">
        <CommunityHub
          slug={doc.slug}
          city={doc.city || doc.state || doc.country}
          headerSlot={
            <div className="space-y-2">
              {/* Hyperlinked crumb chain (Country → State → City) */}
              <div className="text-xs font-mono uppercase tracking-widest flex items-center gap-1 flex-wrap opacity-90">
                <BookOpenText className="w-3 h-3" />
                <CrumbLinks doc={doc} palette={palette} />
              </div>
              {/* Native name + capital line — only render when present */}
              {(doc.native_name && doc.native_name !== doc.name) && (
                <div className="text-lg font-display opacity-80">{doc.native_name}</div>
              )}
              {doc.capital && (
                <div className="text-sm inline-flex items-center gap-1">
                  <MapPin className="w-4 h-4" /> Capital: <strong>{doc.capital}</strong>
                </div>
              )}
              {/* Browse-topics dropdown lives inside the header now */}
              {populated.length > 0 && (
                <TopicsDropdown
                  slug={doc.slug}
                  populated={populated}
                  palette={palette}
                />
              )}
              {/* Screen-reader-only H1 so search-engines still see the
                  location name as a top-level heading (was previously
                  the huge display h1 in the deleted second panel). */}
              <h1 className="sr-only" data-testid="location-title">{doc.name}</h1>
            </div>
          }
        />
      </div>

      {/* Places directory link — hospitals, schools, worship, parks, charities, food banks.
          The whole orange strip is now a link; the "Explore →" pill is a plain visual
          affordance rendered as a span so we don't nest anchor tags. */}
      <Link
        to={`/places/${doc.slug}`}
        className="mb-6 flex items-center justify-between rounded-xl border px-5 py-4 hover:shadow-md transition group"
        style={{ borderColor: "var(--wofy-border)", background: "linear-gradient(135deg, #FFF7ED 0%, #FEE9D7 100%)" }}
        data-testid="location-places-cta"
      >
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest opacity-70">Community-verified directory</div>
          <div className="font-display text-lg leading-tight group-hover:underline decoration-2 underline-offset-4">
            Places to know in {doc.city || doc.name}
          </div>
          <div className="text-xs opacity-70 mt-0.5">Hospitals · Schools · Worship · Parks · Charities · Food Banks</div>
        </div>
        <span
          className="rounded-full px-4 py-2 text-sm font-semibold text-white shadow-sm group-hover:shadow-md transition inline-flex items-center whitespace-nowrap"
          style={{ background: "var(--wofy-primary)" }}
          data-testid="location-places-cta-btn"
        >
          Explore →
        </span>
      </Link>

      <div className="grid lg:grid-cols-[220px_1fr_260px] gap-6">
        {/* LEFT — jump-to-section rail */}
        <aside className="hidden lg:block sticky top-6 self-start" data-testid="location-toc">
          <div className="overline mb-2" style={{ color: "var(--wofy-primary)" }}>
            <Sparkles className="w-3 h-3 inline -mt-0.5 mr-1" /> ON THIS PAGE
          </div>
          <nav className="text-sm space-y-1">
            {populated.map((k) => (
              // Two affordances rolled into one item:
              //   • Left column = **scroll-to** anchor (preserves the
              //     original bookmark UX for anyone reading top-to-bottom)
              //   • Right column = **↗** link to the standalone topic
              //     page (permalink, shareable, indexable)
              <div key={k} className="flex items-center gap-1" data-testid={`toc-row-${k}`}>
                <a
                  href={`#s-${k}`}
                  className="flex-1 px-3 py-1.5 rounded-lg hover:bg-black/[0.05] transition"
                  data-testid={`toc-${k}`}
                >
                  {SECTION_LABELS[k]}
                </a>
                <Link
                  to={`/lt/${doc.slug}/topic/${k}`}
                  className="p-1.5 rounded-lg text-xs opacity-60 hover:opacity-100 hover:bg-black/[0.05] transition"
                  title={`Open ${SECTION_LABELS[k]} as a standalone page`}
                  aria-label={`Open ${SECTION_LABELS[k]} as a standalone page`}
                  data-testid={`toc-link-${k}`}
                >
                  ↗
                </Link>
              </div>
            ))}
          </nav>
        </aside>

        {/* CENTER — body */}
        <div className="min-w-0" data-testid="location-body">
          {populated.length === 0 && (
            <div className="wofy-card text-center py-10 opacity-70">
              <p className="text-sm">This location article is still a stub.</p>
              <p className="text-xs mt-2">Content will appear here once an editor fills it in.</p>
            </div>
          )}
          {populated.map((k) => (
            <section key={k} id={`s-${k}`} className="mb-8 scroll-mt-20" data-testid={`section-${k}`}>
              <h2 className="font-display text-2xl mb-3 pb-2 border-b flex items-center justify-between gap-2" style={{ borderColor: `${palette.accent}44`, color: palette.ink }}>
                <span>{SECTION_LABELS[k]}</span>
                <Link
                  to={`/lt/${doc.slug}/topic/${k}`}
                  className="text-xs font-mono uppercase tracking-widest opacity-60 hover:opacity-100 hover:underline whitespace-nowrap"
                  data-testid={`section-permalink-${k}`}
                  title="Open as a standalone page"
                >
                  Open ↗
                </Link>
              </h2>
              <div>{renderBody(sections[k])}</div>
            </section>
          ))}
        </div>

        {/* RIGHT — links */}
        <aside className="lg:sticky lg:top-6 self-start" data-testid="location-links">
          <div className="wofy-card">
            <div className="overline mb-3" style={{ color: "var(--wofy-primary)" }}>
              <Globe className="w-3 h-3 inline -mt-0.5 mr-1" /> USEFUL LINKS
            </div>
            {(doc.links || []).length === 0 ? (
              <p className="text-xs opacity-60">No external links added yet.</p>
            ) : (
              <ul className="space-y-2">
                {(doc.links || []).map((l, i) => {
                  const s = slugifyLinkCaption(l.caption);
                  const color = LINK_KIND_COLOR[l.kind] || "var(--wofy-text)";
                  return (
                    <li key={i} className="flex items-start gap-1.5">
                      {/* Primary label → internal AI reference page */}
                      <Link
                        to={`/lr/${doc.slug}/${s}`}
                        className="flex-1 text-sm hover:underline inline-flex items-start gap-1.5 group"
                        style={{ color }}
                        data-testid={`location-link-${i}`}
                        title={`Read about ${l.caption}`}
                      >
                        <BookOpenText className="w-3.5 h-3.5 mt-0.5 shrink-0 opacity-60 group-hover:opacity-100" />
                        <span>{l.caption}</span>
                      </Link>
                      {/* Secondary icon → go straight to the external URL */}
                      <a
                        href={l.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-0.5 opacity-50 hover:opacity-100 shrink-0"
                        style={{ color }}
                        aria-label={`Open ${l.caption} in a new tab`}
                        title="Open external site"
                        data-testid={`location-link-external-${i}`}
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </aside>
      </div>

      {/* State / country landing directories — surfaces every child
          city/district so `/l/india/kerala` becomes a browsable hub. */}
      {(doc.kind === "state" || doc.kind === "country") && (
        <StatePlaces slug={doc.slug} name={doc.name} />
      )}

      {/* Diaspora journey chip strip — invites Indian city readers to
          explore neighbouring-country trips. Auto-hidden for slugs
          without a curated pairing. */}
      {doc.kind === "city" && doc.slug?.startsWith("india/") && (
        <DiasporaJourney slug={doc.slug} name={doc.name} />
      )}

      {/* Live local news headlines — Google News RSS, cached hourly.
          Auto-hides when no items are found. */}
      {(doc.kind === "city" || doc.kind === "town" || doc.kind === "county") && (
        <LocalNewsFeed slug={doc.slug} />
      )}

      {/* Publisher widget — surfaces canonical blog posts distributed to
          this location. Widget fails-open when there are no matches, so
          nothing renders for locations without curated content. */}
      {(() => {
        const locId = publisherLocationIdFor(doc);
        if (!locId) return null;
        return (
          <div className="mt-6" data-testid="location-publisher-widget">
            <PublisherWidget
              siteId="newspulse"
              locationId={locId}
              title={`From the Publisher — ${doc.name}`}
              limit={6}
              variant="grid"
              testIdPrefix="publisher-widget-location"
            />
          </div>
        );
      })()}

      {/* ChatPulse — location-scoped discussion. Same slug both ways
          (e.g. `india/kerala/pala`) so a comment made from the location
          hub is discoverable on the canonical page and vice-versa. */}
      <ChatPulseThread
        contextType="location"
        contextId={doc.slug || doc.id}
        contextLabel={doc.name}
      />

      {/* Weather activity — surfaces AI almanac records + citizen weather
          reports for this location. Auto-hides when there's nothing yet. */}
      <LocationWeatherPanel slug={doc.slug} />

      {/* Floating "Post here" FAB — always visible while reading the article so
          users can drop a Pulse / Event / Cause for this location without
          scrolling back up. */}
      <LocalPostFab city={doc.city || doc.state || doc.country || doc.name} />
      </>)}
    </article>
  );
}

/**
 * CrumbLinks — hyperlinked breadcrumb inside the coloured location
 * header. Every crumb segment (Country / State / City) becomes a
 * real link back to its own location article, so the badge always
 * routes to the main page of that place.
 */
function CrumbLinks({ doc }) {
  const segs = (doc.slug || "").split("/").filter(Boolean);
  const labels = [];
  if (doc.country) labels.push(doc.country);
  if (doc.state)   labels.push(doc.state);
  if (doc.city && doc.kind !== "country" && doc.kind !== "state") labels.push(doc.city);

  const hrefs = segs.map((_, i) => "/l/" + segs.slice(0, i + 1).join("/"));
  return (
    <>
      <Link
        to={`/l/${doc.slug}`}
        className="hover:underline opacity-90"
        data-testid="crumb-kind"
      >
        {doc.kind}
      </Link>
      {labels.map((label, i) => (
        <span key={i} className="inline-flex items-center gap-1">
          <span className="mx-1 opacity-40">·</span>
          <Link
            to={hrefs[i] || "#"}
            className="hover:underline opacity-90"
            data-testid={`crumb-${i}`}
          >
            {label}
          </Link>
        </span>
      ))}
    </>
  );
}

/**
 * TopicsDropdown — mobile-friendly disclosure widget mounted inside
 * the coloured location header. Lets readers jump straight to any
 * standalone topic page without hunting through the sidebar TOC.
 *
 * We render as a native `<details>` element for zero-JS keyboard and
 * screen-reader support.
 */
function TopicsDropdown({ slug, populated, palette }) {
  return (
    <details
      className="mt-4 rounded-lg border overflow-hidden inline-block"
      style={{ borderColor: `${palette.accent}55`, background: "#ffffffcc" }}
      data-testid="location-topics-dropdown"
    >
      <summary
        className="cursor-pointer select-none px-3 py-2 text-xs font-semibold uppercase tracking-widest inline-flex items-center gap-2"
        style={{ color: palette.ink }}
        data-testid="location-topics-dropdown-toggle"
      >
        Browse topics · {populated.length}
      </summary>
      <div className="max-h-72 overflow-y-auto p-2 grid sm:grid-cols-2 gap-1 min-w-[280px]">
        {populated.map((k) => (
          <Link
            key={k}
            to={`/lt/${slug}/topic/${k}`}
            className="text-sm px-2 py-1 rounded hover:bg-black/[0.05]"
            data-testid={`topics-dd-${k}`}
            style={{ color: palette.ink }}
          >
            {SECTION_LABELS[k]}
          </Link>
        ))}
      </div>
    </details>
  );
}
